/*************************************************************************
 * ADOBE SYSTEMS INCORPORATED
 *  Copyright 2024 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this file in
 * accordance with the terms of the Adobe license agreement accompanying it.
 * If you have received this file from a source other than Adobe, then your
 * use, modification, or distribution of it requires the prior written
 * permission of Adobe.
 **************************************************************************/
import EventEmitter from 'eventemitter3';
import Deferred from './Deferred';

// Singleton event emitter instance to handle global events
const eventEmitter = new EventEmitter();

// Track provider dependencies and dropins
const providersDependencies = new Map();

/**
 * Utility function to get the default dependency map for a provider
 * @returns {Object} A default object structure with empty providers and dropins arrays.
 */
const getDefaultDependencyMap = () => ({
  providers: [],
  dropins: [],
});

/**
 * Method to notify a provider and track changes in dependent providers.
 * @param {string} providerName - Name of the provider to notify.
 * @param {Object} event - Event object containing event type and data.
 * @param {Map} changedProvidersMap - A map to track the providers that have changed.
 */
const notifyAndTrack = async (providerName, event, changedProvidersMap) => {
  // Handling a cyclic dependency between the providers
  if (changedProvidersMap.get(providerName)?.type === event?.type) {
    return;
  }

  // Add the provider to the changedProvidersMap
  changedProvidersMap.set(providerName, event);

  // Retrieve dependencies (providers and dropins) for this provider
  const dependenciesMap = providersDependencies.get(providerName) || getDefaultDependencyMap();

  // Notify dependent providers
  const providersPromises = dependenciesMap.providers.map(depProvider => depProvider.listener(event)
    .then(impactEvent => {
      const impactEventArgument = impactEvent ?? event;
      return notifyAndTrack(depProvider.name, impactEventArgument, changedProvidersMap);
    }));

  await Promise.all(providersPromises); // Wait for all dependent providers to be updated
};

/**
 * Notify dropins about changes in providers.
 * @param {Map} changedProviders - A map of providers that have changed and the associated event data.
 */
const notifyDropins = changedProviders => {
  // Notify each dropin for the changed providers
  changedProviders.forEach((event, providerName) => {
    const dependenciesMap = providersDependencies.get(providerName) || getDefaultDependencyMap();
    dependenciesMap.dropins.forEach(depDropin => {
      depDropin.listener(event);
    });
  });
};

/**
 * @classdesc
 * Global Context Manager (GCM)
 * This class is responsible for managing dependencies between providers and dropins.
 * @class
 */
class GlobalContextManager {
  /**
   * Registers a provider or dropin with its dependencies.
   * @param {string} providerName - The name of the provider.
   * @param {function} listener - The listener function for this provider/dropin.
   * @param {Object} options - Configuration options.
   * @param {boolean} options.isProvider - Indicates whether the entity is a provider.
   * @param {string} options.name - The name of the provider or dropin.
   */
  onProviderChange(providerName, listener, { isProvider, name }) {
    const dependenciesMap = providersDependencies.get(providerName) || getDefaultDependencyMap();
    const dependencies = dependenciesMap[isProvider ? 'providers' : 'dropins'];

    dependencies.push({ name, listener });
    providersDependencies.set(providerName, dependenciesMap);
  }

  /**
   * Unregisters a provider or dropin.
   * @param {string} providerName - The name of the provider.
   * @param {function} listener - The listener function to remove.
   * @param {Object} options - Configuration options.
   * @param {boolean} options.isProvider - Indicates whether the entity is a provider.
   * @param {string} options.name - The name of the provider or dropin.
   */
  offProviderChange(providerName, listener, { isProvider, name }) {
    const dependenciesMap = providersDependencies.get(providerName) || getDefaultDependencyMap();
    const dependencies = dependenciesMap[isProvider ? 'providers' : 'dropins'];

    // Filter out the listener to be removed
    dependenciesMap[isProvider ? 'providers' : 'dropins'] = dependencies.filter(
      dependency => dependency.name !== name || dependency.listener !== listener,
    );
    providersDependencies.set(providerName, dependenciesMap);
  }

  /**
   * Emits a provider change event, triggering a reconciliation of all dependent providers.
   * @param {string} providerName - The provider being changed.
   * @param {Object} event - The event data (type and details) related to the change.
   * @param {Object} options - Options like timeout
   */
  async emitProviderChange(providerName, event, options = {}) {
    const executeEmitProviderChange = async () => {
      const changedProviders = new Map(); // Track all providers that have changed
      await notifyAndTrack(providerName, event, changedProviders);
      // Notify dropins about the changed providers
      notifyDropins(changedProviders);
    };

    const { timeout = 3000 } = options;
    const emitProviderTimeout = new Deferred();
    setTimeout(() => emitProviderTimeout.reject(new Error('Execution time exceeded timeout')), timeout);
    await Promise.race([executeEmitProviderChange(), emitProviderTimeout.promise]);
  }

  /**
   * Register for any global event via EventEmitter.
   * @param {string} eventName - The name of the global event.
   * @param {function} listener - The listener function for the event.
   * @param {Object} context - context or 'this' value that should be set for emitted events
   */
  on(eventName, listener, context) {
    eventEmitter.on(eventName, listener, context);
  }

  /**
   * Remove a listener for a global event.
   * @param {string} eventName - The name of the global event.
   * @param {function} listener - The listener function to remove.
   * @param {Object} context - context or 'this' value that should be set for emitted events
   */
  off(eventName, listener, context) {
    eventEmitter.off(eventName, listener, context);
  }

  /**
   * Emit a global event to notify listeners.
   * @param {string} eventName - The event name.
   * @param {Object} options - Configuration options.
   * @param {boolean} [options.isGlobalEvent=false] - Flag to indicate if it's a global event.
   * @param {...any} args - The arguments to pass to the event listener.
   */
  emit(eventName, { isGlobalEvent = false }, ...args) {
    if (isGlobalEvent) {
      // Handle global event via Broadcast Channel if needed
    }
    eventEmitter.emit(eventName, ...args);
  }
}

const globalContextManager = new GlobalContextManager();
export default globalContextManager;
